import { createApiClient, createTokenStorage } from '@space/shared-api-client'
import { handleJSMessage } from '@/shared/utils/jsMessageHandler'
import { CAPTCHA_VISIBLE_REQUIRED_CODE, LOGIN_SESSION_EXPIRED } from '@/shared/utils/captchaRetryService'
import { captureEmailCode } from '@/shared/utils/devEmailCodeCapture'
import { clearProfileCache } from '@/modules/profile/services/profileCache'

/**
 * Determine API base URL:
 * 1. Use window.__API_BASE_URL__ if set (configured in HTML at build time)
 * 2. Use VITE_GONKAGAME_API_URL env variable (for development override)
 * 3. Fall back to relative /api (works with same-origin requests)
 */
export const getApiBaseUrl = (): string => {
  // Production: use build-time configured API endpoint from HTML
  if (typeof window !== 'undefined' && (window as any).__API_BASE_URL__) {
    return (window as any).__API_BASE_URL__
  }

  // Development: allow env override
  if (import.meta.env.VITE_GONKAGAME_API_URL) {
    return import.meta.env.VITE_GONKAGAME_API_URL
  }

  // Default: relative /api for all domains (same-origin requests)
  return '/api'
}

const baseURL = getApiBaseUrl()
const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'

const tokenStorage = createTokenStorage({ storageKey })

/**
 * Токен входа для ручек, которые ходят мимо общего клиента (`fetch` к `/app/*`).
 * Ключ хранилища остаётся ровно один — второй разошёлся бы с этим молча.
 */
export const getAuthToken = (): string | null => tokenStorage.getToken()

/**
 * Handle 401 Unauthorized: clear auth state and conditionally redirect.
 * On public routes (docs, events, landing) — just clear the token silently.
 * On protected routes — redirect to landing page.
 */
export const handleUnauthorized = () => {
  console.warn('[gonkagame] 401 Unauthorized - logging out user')

  // Clear all auth-related data
  tokenStorage.setToken('')
  // Кеш «кто вошёл» — тоже часть авторизации: оставить его здесь значит
  // рисовать шапку по данным аккаунта, из которого человека уже выкинули.
  clearProfileCache()
  localStorage.removeItem(storageKey)
  localStorage.removeItem('user_avatar')
  localStorage.removeItem('partner_bonus_split')
  localStorage.removeItem('is_ambassador')

  // Clear matrix module localStorage cache (CR-MTX-07)
  Object.keys(localStorage)
    .filter(key => key.startsWith('/api/matrix/'))
    .forEach(key => localStorage.removeItem(key))

  // On public routes, just clear the stale token — don't redirect.
  // Use pathname check instead of router import to avoid circular module dependencies.
  // IGRA-110: `/invite` — публичная страница приглашения в общую подписку.
  // Гость приходит сюда по ссылке из письма, и протухший токен в его браузере
  // не повод уводить на «/»: вместе со страницей он потерял бы и само
  // приглашение — второй такой ссылки ему никто не пришлёт.
  // `/subscription/cancel` — экран отмены по ссылке из письма: протухшая
  // ограниченная сессия показывает там «войдите», а не выкидывает на лендинг.
  const publicPrefixes = ['/docs', '/events', '/event-reward', '/r/', '/registration', '/invite', '/subscription/cancel']
  const isPublicPath = publicPrefixes.some(p => window.location.pathname.startsWith(p))
  if (!isPublicPath && window.location.pathname !== '/') {
    window.location.href = '/'
  }
}

const client = createApiClient({
  baseURL,
  tokenStorage,
  spaceId: import.meta.env.VITE_X_SPACE_ID,
  onUnauthorized: handleUnauthorized,
  // @ts-ignore: Assuming underlying client supports axios config merging or specific option
  withCredentials: true,
  headers: {
    'x-expect-format': 'json',
  },
})

/** Checkout grants are applied by the caller only after its stale-response guard. */
export const sessionlessHttpClient = createApiClient({
  baseURL,
  tokenStorage: {
    getToken: () => null,
    setToken: () => {},
    clearToken: () => {},
  },
  spaceId: import.meta.env.VITE_X_SPACE_ID,
  expectFormat: 'json',
})

/**
 * OTP-R3: транспорт для запросов входа (opt-in). Исходящий x-auth читается из
 * живого хранилища, но x-token ответа клиент сам не сохраняет: его принимает
 * guardedRequest, и только если попытка входа ещё актуальна.
 */
const guardedClient = createApiClient({
  baseURL,
  spaceId: import.meta.env.VITE_X_SPACE_ID,
  tokenStorage: {
    getToken: () => tokenStorage.getToken(),
    setToken: () => {},
    clearToken: () => {},
  },
})

/** Ответ устаревшей попытки входа: вызывающий должен молча его отбросить. */
export class AuthRequestCancelled extends Error {
  constructor() {
    super('Stale auth request')
    this.name = 'AuthRequestCancelled'
  }
}

const assertCurrent = (isCurrent?: () => boolean): void => {
  if (isCurrent && !isCurrent()) throw new AuthRequestCancelled()
}

const acceptResponseToken = (response: Response): void => {
  const token = response.headers.get('x-token') ?? response.headers.get('x-auth')
  if (token) tokenStorage.setToken(token)
}

/**
 * OTP-R6c: ответ входа с `requires_approval` несёт pending-ключ — это ещё не
 * вход (ждёт код из приложения). Его держит вызывающий и шлёт только на
 * /profile/login-approval; в хранилище входа он не попадает.
 */
const isPendingApproval = (body: unknown): boolean =>
  (body as { data?: { requires_approval?: unknown } } | null)?.data?.requires_approval === true

/**
 * Wrap client methods to intercept JSMessage in responses and handle 401
 */
const wrapClient = (apiClient: typeof client) => {
  const originalGet = apiClient.get.bind(apiClient)
  const originalPost = apiClient.post.bind(apiClient)
  const originalPut = apiClient.put.bind(apiClient)
  const originalPatch = apiClient.patch.bind(apiClient)
  const originalDelete = apiClient.delete.bind(apiClient)
  const originalRequestRaw = apiClient.requestRaw.bind(apiClient)

  const intercept = async <T>(promise: Promise<T>, isCurrent?: () => boolean, endpoint?: string): Promise<T> => {
    try {
      const result = await promise
      assertCurrent(isCurrent)
      handleJSMessage(result)
      return result
    } catch (error: any) {
      // Ответ отменённой попытки входа: ни logout, ни тостов, ни перезагрузки.
      if (error instanceof AuthRequestCancelled) throw error
      // Check for 401 status and trigger logout
      const status = error?.response?.status ?? error?.status
      if (status === 401) {
        handleUnauthorized()
        throw error
      }

      if (error?.response) {
        // Many legacy errors come as 4xx/5xx with JSMessage in body
        try {
          const data = await error.response.json()
          assertCurrent(isCurrent)

          // Check for LOGIN_SESSION_EXPIRED (error_code: 22022)
          const isSessionExpired = data?.error_code === LOGIN_SESSION_EXPIRED || data?.errorCode === LOGIN_SESSION_EXPIRED
          if (isSessionExpired) {
            console.warn('[gonkagame] Login session expired - clearing auth and reloading')

            // Clear all auth-related data
            tokenStorage.setToken('')
            clearProfileCache()
            localStorage.removeItem(storageKey)
            localStorage.removeItem('user_avatar')
            localStorage.removeItem('partner_bonus_split')
            localStorage.removeItem('is_ambassador')

            // Clear auth cookie
            document.cookie = 'dasdasdaddjdj=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

            // Reload application to reset state
            window.location.reload()
            throw error
          }

          // Skip toast for errors that frontend handles specially
          // 11013 (HK-IGRA.940) — невидимая проверка не прошла: форма сама
          // покажет видимую и повторит запрос, тост здесь лишний.
          const isCaptchaExpired = data?.error_code === 11011 ||
            data?.errorCode === 11011 ||
            data?.error_code === CAPTCHA_VISIBLE_REQUIRED_CODE ||
            data?.errorCode === CAPTCHA_VISIBLE_REQUIRED_CODE
          const isEmailExists = data?.error_code === 11012 ||
            data?.errorCode === 11012
          // Marketplace ownership/approval reconciliation errors come back as a
          // generic 400 with English `error_message`. The buy-modal localises
          // and presents these — the auto-toast here would steal the slot with
          // an English JSMessage. Skip on substring match.
          const rawMsg = typeof data?.error_message === 'string'
            ? data.error_message.toLowerCase()
            : (typeof data?.error_details === 'string'
              ? data.error_details.toLowerCase()
              : '')
          const isMarketplaceFlowError =
            rawMsg.includes('seller no longer holds') ||
            rawMsg.includes('no longer owns') ||
            rawMsg.includes('seller has revoked') ||
            rawMsg.includes('approval revoked') ||
            rawMsg.includes('left escrow custody') ||
            rawMsg.includes('listing has been cancelled') ||
            rawMsg.includes('cannot purchase your own')
          // Шаг TOTP сам пишет под полем «код не подошёл» (0) и «много попыток» (31):
          // тост с текстом сервера повторил бы то же самое второй раз.
          const isShownByTotpStep = endpoint === '/profile/login-approval' &&
            (data?.error_code === 0 || data?.error_code === 31)
          // F1: лимиты кода (32 — код сожжён, 33 — много запросов) окно входа
          // по коду само объясняет под полем.
          const isShownByCodeForm = (endpoint === '/profile/login-by-code/verify' || endpoint === '/profile/signup') &&
            (data?.error_code === 32 || data?.error_code === 33)
          if (!isCaptchaExpired && !isEmailExists && !isMarketplaceFlowError && !isShownByTotpStep && !isShownByCodeForm) {
            handleJSMessage(data)
          }
          // Attach parsed data to error for downstream handlers
          error.response.data = data
        } catch (e) {
          if (e instanceof AuthRequestCancelled) throw e
          // Fallback if not json
        }
      }
      throw error
    }
  }

  /**
   * OTP-R3: запрос входа, чьи эффекты (x-token, тосты, 401/22022) применяются
   * только пока `isCurrent()` истинно. Устаревший ответ даёт AuthRequestCancelled.
   */
  const guardedRequest = <T>(endpoint: string, isCurrent: () => boolean, options?: Parameters<typeof apiClient.requestRaw>[1]): Promise<T> => {
    const request = async (): Promise<T> => {
      assertCurrent(isCurrent)
      try {
        const { data, response } = await guardedClient.requestRaw<T>(endpoint, options)
        assertCurrent(isCurrent)
        if (!isPendingApproval(data)) acceptResponseToken(response)
        return data
      } catch (error: any) {
        assertCurrent(isCurrent)
        // Как и обычный клиент: x-token не-401 ответа тоже сохраняется.
        if (error?.response && error.response.status !== 401) acceptResponseToken(error.response)
        throw error
      }
    }

    return intercept(request(), isCurrent, endpoint)
  }

  /**
   * Intercept raw requests and capture email-code header (dev only)
   */
  const interceptRaw = async <T>(promise: Promise<{ data: T; response: Response }>): Promise<{ data: T; response: Response }> => {
    try {
      const result = await promise
      handleJSMessage(result.data)

      // [DEV ONLY] Capture email-code header from security API responses
      captureEmailCode(result.response)

      return result
    } catch (error: any) {
      // Check for 401 status and trigger logout
      const status = error?.response?.status ?? error?.status
      if (status === 401) {
        handleUnauthorized()
        throw error
      }

      if (error?.response) {
        try {
          const data = await error.response.json()
          handleJSMessage(data)
          error.response.data = data
        } catch (e) {
          // Fallback if not json
        }
      }
      throw error
    }
  }

  apiClient.get = (endpoint, options) => intercept(originalGet(endpoint, options))
  apiClient.post = (endpoint, body, options) => intercept(originalPost(endpoint, body, options))
  apiClient.put = (endpoint, body, options) => intercept(originalPut(endpoint, body, options))
  apiClient.patch = (endpoint, body, options) => intercept(originalPatch(endpoint, body, options))
  apiClient.delete = (endpoint, options) => intercept(originalDelete(endpoint, options))

  /**
   * POST request that returns both data and response (for header access)
   * Used for security endpoints that may include email-code header in dev mode
   */
  const postRaw = async <T>(endpoint: string, body?: unknown, options?: Parameters<typeof apiClient.post>[2]): Promise<{ data: T; response: Response }> => {
    return interceptRaw<T>(originalRequestRaw<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body as any,
    }))
  }

  // Explicitly expose token methods (they exist on apiClient from createApiClient)
  return {
    ...apiClient,
    postRaw,
    guardedRequest,
    getToken: () => apiClient.getToken(),
    setToken: (token: string) => apiClient.setToken(token),
    clearToken: () => apiClient.clearToken(),
  }
}

export const httpClient = wrapClient(client)



